import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import { useNavigate } from "react-router-dom"
import * as Yup from "utils/yup"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import {
  getDeliveryProfilesScope,
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import ConfirmModal from "components/Common/ConfirmModal"
import {
  getImagePreviewSrc2,
  handleFileChange2,
} from "components/Shared/fileHandlers"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import BrandDeliverySettingsCards from "components/Settings/BrandDeliverySettingsCards"

const initialValues = {
  iconUrl: "",
  iconType: "UPLOAD",
  iconFile: null,
}

const DeliverySettings = () => {
  const { t } = useTranslation()
  document.title = t("Delivery Setting")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, isLoading, fastMovingDeliverySettings, deliveryProfilesScope } =
    useSelector(state => state.GlobalSettings)
  const { userRole, accountType } = useSelector(state => state.Login)
  const { isImageCompressing } = useSelector(state => state.Common)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })
  const [formValues, setFormValues] = useState(initialValues)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [pendingValues, setPendingValues] = useState(null)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      50,
      "order_cancel_settings_3",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole, accountType, navigate])

  useEffect(() => {
    dispatch(getGlobalSettingByType({ type: "fastMovingDeliverySettings" }))
    dispatch(getDeliveryProfilesScope())
  }, [dispatch])

  const showFastMovingIcon = useMemo(() => {
    const cards = deliveryProfilesScope?.cards ?? []
    return cards.some(card => card.mode === "hybrid_ecommerce")
  }, [deliveryProfilesScope])

  useEffect(() => {
    if (fastMovingDeliverySettings?.fastMovingDeliverySettings) {
      const { iconUrl, iconUrlType } =
        fastMovingDeliverySettings.fastMovingDeliverySettings
      setFormValues({
        iconUrl: iconUrl || "",
        iconType: iconUrlType || "UPLOAD",
        iconFile: null,
      })
    }
  }, [fastMovingDeliverySettings])

  const validationSchema = Yup.object({
    iconType: Yup.string().optional(),
    iconUrl: Yup.string().optional().nullable(),
    iconFile: Yup.mixed().nullable().optional(),
  })

  const handleOnSubmit = values => {
    setPendingValues(values)
    setShowSaveModal(true)
  }

  const confirmSave = () => {
    if (!pendingValues) return
    const { iconUrl, iconType, iconFile } = pendingValues
    const existingSettings =
      fastMovingDeliverySettings?.fastMovingDeliverySettings || {}

    const data = new FormData()
    data.append("id", fastMovingDeliverySettings._id)
    data.append("type", "fastMovingDeliverySettings")
    data.append("enabled", existingSettings.enabled === true)
    data.append(
      "sameDayCutOffTime",
      existingSettings.sameDayCutOffTime || "13:00"
    )
    data.append(
      "sameDayMinimumSlotStartTime",
      existingSettings.sameDayMinimumSlotStartTime || "17:00"
    )
    data.append("iconUrlType", iconType)
    data.append("iconUrl", iconUrl || "")
    if (iconType === "UPLOAD" && iconFile) {
      data.append("image", iconFile)
    }

    dispatch(updateGlobalSettingByType(data))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowSaveModal(false)
          dispatch(
            getGlobalSettingByType({ type: "fastMovingDeliverySettings" })
          )
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const getIconPreview = values => {
    if (values.iconType === "UPLOAD" && values.iconFile) {
      return getImagePreviewSrc2(values.iconFile)
    }
    if (values.iconUrl) {
      return values.iconUrl
    }
    return null
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <BrandDeliverySettingsCards
          settingsId={fastMovingDeliverySettings?._id}
          canEdit={usersPermissions.edit}
        />

        {showFastMovingIcon ? (
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, setFieldValue }) => (
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Fast-moving item icon")}</CardTitle>
                    <p className="text-muted">
                      {t(
                        "This icon is shown on the customer app over items marked as fast-moving. Delivery timing for hybrid brands is configured in the brand cards above."
                      )}
                    </p>
                    <Form>
                      <Row className="mb-4">
                        <Col md={6}>
                          <div className="form_field">
                            <Label htmlFor="iconType">{t("Icon source")}</Label>
                            <Field
                              as="select"
                              id="iconType"
                              name="iconType"
                              className="form-control"
                              disabled={!usersPermissions.edit}
                              onChange={e => {
                                const selectedValue = e.target.value
                                setFieldValue("iconType", selectedValue)
                                setFieldValue("iconUrl", "")
                                setFieldValue("iconFile", null)
                              }}
                            >
                              <option value="URL">{t("Image Url")}</option>
                              <option value="UPLOAD">
                                {t("Upload Image")}
                              </option>
                            </Field>
                            <TextError name="iconType" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            {values.iconType === "URL" ? (
                              <>
                                <Label htmlFor="iconUrl">{t("URL")}</Label>
                                <Field
                                  type="text"
                                  id="iconUrl"
                                  name="iconUrl"
                                  className="form-control"
                                  disabled={!usersPermissions.edit}
                                />
                                <TextError name="iconUrl" />
                              </>
                            ) : (
                              <div className="form_field position-relative">
                                <Label htmlFor="iconFile">
                                  {t("Upload Image")}
                                </Label>
                                <TranslatedFileInput
                                  id="iconFile"
                                  name="iconFile"
                                  accept="image/jpeg, image/jpg, image/png"
                                  disabled={!usersPermissions.edit}
                                  statusText={
                                    values.iconUrl && !values.iconFile
                                      ? t("Current icon will be kept")
                                      : undefined
                                  }
                                  onChange={e => {
                                    handleFileChange2(
                                      e,
                                      "iconFile",
                                      setFieldValue
                                    )
                                  }}
                                />
                                {values.iconUrl && !values.iconFile ? (
                                  <small className="text-muted d-block mt-1">
                                    {t(
                                      "Leave empty to keep the current icon when saving other changes."
                                    )}
                                  </small>
                                ) : null}
                                {isImageCompressing ? (
                                  <Spinner
                                    color="primary"
                                    className="file_upload_btn"
                                  />
                                ) : null}
                                <TextError name="iconFile" />
                              </div>
                            )}
                            {getIconPreview(values) ? (
                              <div className="mt-3">
                                <img
                                  src={getIconPreview(values)}
                                  alt={t("Fast-moving item icon preview")}
                                  className="img_preview"
                                />
                              </div>
                            ) : null}
                          </div>
                        </Col>
                      </Row>

                      {usersPermissions.edit ? (
                        <div className="d-flex justify-content-end">
                          <Button
                            color="primary"
                            type="submit"
                            disabled={isLoading || isImageCompressing}
                          >
                            {isLoading ? t("Saving...") : t("Save")}
                          </Button>
                        </div>
                      ) : null}
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Formik>
        ) : null}

        {showFastMovingIcon ? (
        <ConfirmModal
          show={showSaveModal}
          onDeleteClick={confirmSave}
          onCloseClick={() => setShowSaveModal(false)}
          isLoading={isLoading}
          buttonText={t("Yes")}
          bodyTitle={t(
            "Are you sure you want to save the fast-moving item icon?"
          )}
        />
        ) : null}
      </div>
    </div>
  )
}

export default DeliverySettings
